import { useCallback, useMemo } from "react";
import {
  DocumentNode,
  LazyQueryHookOptions,
  MutationHookOptions,
  OperationVariables,
  QueryHookOptions,
  TypedDocumentNode,
  useLazyQuery as useApolloLazyQuery,
  useMutation as useApolloMutation,
  useQuery as useApolloQuery,
} from "@apollo/client";
import cloneDeep from "lodash/cloneDeep";

/*
 * Apollo Client 3 hands out results that are shared with its cache and frozen, while this code base was written for
 * Apollo Client 2 and sorts or edits results in place. These wrappers give each component its own copy, made once
 * per new result, so that code keeps working without touching the cache.
 */

type Query = DocumentNode | TypedDocumentNode<any, any>;

const copy = <T>(value: T): T => (value == null ? value : cloneDeep(value));

function withCopiedCallbacks<O extends { onCompleted?: (data: any, ...rest: any[]) => void }>(options?: O): O | undefined {
  if (!options?.onCompleted) return options;
  const { onCompleted } = options;
  return { ...options, onCompleted: (data: any, ...rest: any[]) => onCompleted(copy(data), ...rest) };
}

export function useQuery<TData = any, TVariables extends OperationVariables = OperationVariables>(
  query: Query,
  options?: QueryHookOptions<TData, TVariables>,
) {
  const result = useApolloQuery<TData, TVariables>(query, withCopiedCallbacks(options));
  const data = useMemo(() => copy(result.data), [result.data]);
  const previousData = useMemo(() => copy(result.previousData), [result.previousData]);
  return { ...result, data, previousData };
}

export function useLazyQuery<TData = any, TVariables extends OperationVariables = OperationVariables>(
  query: Query,
  options?: LazyQueryHookOptions<TData, TVariables>,
) {
  const [execute, result] = useApolloLazyQuery<TData, TVariables>(query, withCopiedCallbacks(options));
  const data = useMemo(() => copy(result.data), [result.data]);
  const previousData = useMemo(() => copy(result.previousData), [result.previousData]);
  const run = useCallback(
    (...args: Parameters<typeof execute>) => execute(...args).then((r) => ({ ...r, data: copy(r.data) })),
    [execute],
  ) as typeof execute;
  return [run, { ...result, data, previousData }] as const;
}

export function useMutation<TData = any, TVariables extends OperationVariables = OperationVariables>(
  mutation: Query,
  options?: MutationHookOptions<TData, TVariables>,
) {
  const [execute, result] = useApolloMutation<TData, TVariables>(mutation, withCopiedCallbacks(options));
  const data = useMemo(() => copy(result.data), [result.data]);
  const run = useCallback(
    (...args: Parameters<typeof execute>) => execute(...args).then((r) => ({ ...r, data: copy(r.data) })),
    [execute],
  ) as typeof execute;
  return [run, { ...result, data }] as const;
}
